import { PAGE_POLICIES } from "@medicorepay/shared";

interface EditModuleDraft {
  name: string;
  description: string;
  icon: string;
  sortOrder: number;
}

interface ModuleDetailPanelProps {
  editingModule: string | null;
  editModuleDraft: EditModuleDraft;
  setEditModuleDraft: React.Dispatch<React.SetStateAction<EditModuleDraft>>;
  onSave: () => Promise<any>;
  onCancel: () => void;
  isSaving: boolean;
}

const ICON_OPTIONS = [
  { value: "", label: "No icon" },
  { value: "📊", label: "📊 Reports" },
  { value: "🛒", label: "🛒 Sales" },
  { value: "📦", label: "📦 Inventory" },
  { value: "🏥", label: "🏥 Hospital" },
  { value: "💊", label: "💊 Medicine" },
  { value: "👥", label: "👥 Users" },
  { value: "⚙️", label: "⚙️ Settings" },
  { value: "💳", label: "💳 Payments" },
  { value: "🚚", label: "🚚 Logistics" },
  { value: "🏢", label: "🏢 Business" },
  { value: "📋", label: "📋 Orders" },
  { value: "🏷️", label: "🏷️ Tags" },
  { value: "📅", label: "📅 Calendar" },
  { value: "🔔", label: "🔔 Notifications" },
  { value: "🛡️", label: "🛡️ Security" },
  { value: "🔑", label: "🔑 Keys" },
  { value: "📄", label: "📄 Documents" },
  { value: "💰", label: "💰 Finance" },
  { value: "🧾", label: "🧾 Billing" },
  { value: "🏭", label: "🏭 Warehouse" },
];

export function ModuleDetailPanel({
  editingModule,
  editModuleDraft,
  setEditModuleDraft,
  onSave,
  onCancel,
  isSaving,
}: ModuleDetailPanelProps) {
  const pagePolicyCount = editingModule
    ? PAGE_POLICIES.filter((policy) => policy.requiredModule === editingModule)
        .length
    : 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {editingModule}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {pagePolicyCount} code-owned page policies
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 px-4 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave()}
            disabled={isSaving}
            className="h-9 px-4 rounded-lg bg-teal-600 text-sm text-white font-medium hover:bg-teal-700 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Scrollable Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Name
            </label>
            <input
              value={editModuleDraft.name}
              onChange={(e) =>
                setEditModuleDraft((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              placeholder="e.g., GST Compliance"
              className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Module Icon
            </label>
            <select
              value={editModuleDraft.icon}
              onChange={(e) =>
                setEditModuleDraft((prev) => ({
                  ...prev,
                  icon: e.target.value,
                }))
              }
              className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm"
            >
              {ICON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Description
          </label>
          <input
            value={editModuleDraft.description}
            onChange={(e) =>
              setEditModuleDraft((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            placeholder="What does this module unlock?"
            className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              min={0}
              value={editModuleDraft.sortOrder}
              onChange={(e) =>
                setEditModuleDraft((prev) => ({
                  ...prev,
                  sortOrder: parseInt(e.target.value, 10) || 0,
                }))
              }
              className="w-full h-9 rounded-lg border border-slate-200 px-3 text-sm"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Controls module display ordering (lower = higher)
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
          <h4 className="text-sm font-semibold text-blue-900">
            Routes and sidebar are code-owned
          </h4>
          <p className="mt-1 text-xs leading-5 text-blue-700">
            Change page paths, labels, ordering, icons, modules, and permissions
            in the shared page-policy manifest. Database route/sidebar JSON is
            retained only for migration compatibility and is not runtime
            authority.
          </p>
        </div>
      </div>
    </div>
  );
}
